/**
 * Channel Query Operations
 * Functions for querying and managing chat channels
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { log } from '@/lib/logger';
import type { ChatMessage } from '@/shared/stores/commentStore';
import type { ChatChannelInstance, ChatMessageDocument } from './chatTypes';

const MESSAGES_COLLECTION = 'chat_messages';
const CHANNELS_COLLECTION = 'chat_channels';

/**
 * Reactivate an archived channel for new conversations
 * This allows users to start new conversations on previously resolved rows
 */
export async function reactivateChannel(channelId: string): Promise<void> {
  try {

    const channelsRef = collection(db, CHANNELS_COLLECTION);
    const q = query(
      channelsRef,
      where('channelId', '==', channelId)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const channelDoc = snapshot.docs[0];
      await updateDoc(channelDoc.ref, {
        isActive: true,
        isArchived: false,
        updatedAt: serverTimestamp(),
      });

    } else {
      log.warn(`[CHAT-SERVICE] No archived channel found to reactivate: ${channelId}`);
    }
  } catch (error) {
    log.error('Error reactivating channel:', error);
    throw error;
  }
}

/**
 * Get archived messages for a channel
 */
export async function getArchivedMessages(
  channelId: string,
  limitCount: number = 100
): Promise<ChatMessage[]> {
  try {

    // First check if the channel is archived
    const channelsRef = collection(db, CHANNELS_COLLECTION);
    const channelQuery = query(
      channelsRef,
      where('channelId', '==', channelId)
    );
    const channelSnapshot = await getDocs(channelQuery);

    if (channelSnapshot.empty) {
      log.warn(`[CHAT-SERVICE] Channel not found: ${channelId}`);
      return [];
    }

    const channelData = channelSnapshot.docs[0].data() as ChatChannelInstance;
    if (!channelData.isArchived) {
      log.warn(`[CHAT-SERVICE] Channel is not archived: ${channelId}`);
      return [];
    }

    // Load all messages for the archived channel
    const messagesRef = collection(db, MESSAGES_COLLECTION);
    const q = query(
      messagesRef,
      where('channelId', '==', channelId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const messages: ChatMessage[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as ChatMessageDocument;
      const message: ChatMessage = {
        id: doc.id,
        text: data.text,
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        clientId: data.clientId,
        metadata: data.metadata,
        reactions: data.reactions || {},
      };
      messages.push(message);
    });

    // Return in chronological order (oldest first)
    return messages.reverse();

  } catch (error) {
    log.error('Error loading archived messages:', error);
    throw error;
  }
}

/**
 * Get all archived channels for a workflow
 */
export async function getArchivedChannels(workflowId: string): Promise<ChatChannelInstance[]> {
  try {
    const channelsRef = collection(db, CHANNELS_COLLECTION);
    const q = query(
      channelsRef,
      where('workflowId', '==', workflowId),
      where('isArchived', '==', true),
      orderBy('updatedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const channels: ChatChannelInstance[] = [];

    snapshot.forEach((doc) => {
      channels.push({
        id: doc.id,
        ...doc.data()
      } as ChatChannelInstance);
    });

    return channels;
  } catch (error) {
    log.error('Error getting archived channels:', error);
    return [];
  }
}

/**
 * Get the most recent active channel for a specific entry
 * This handles the case where multiple channels might exist for the same row
 */
export async function getActiveChannelForEntry(
  workflowId: string,
  entryId: string
): Promise<ChatChannelInstance | null> {
  try {
    const channelsRef = collection(db, CHANNELS_COLLECTION);
    const q = query(
      channelsRef,
      where('workflowId', '==', workflowId),
      where('entryId', '==', entryId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as ChatChannelInstance;

  } catch (error) {
    log.error('Error getting active channel for entry:', error);
    return null;
  }
}

/**
 * Get all channels (active and archived) for a specific entry
 * Useful for debugging or admin purposes
 */
export async function getAllChannelsForEntry(
  workflowId: string,
  entryId: string
): Promise<ChatChannelInstance[]> {
  try {
    const channelsRef = collection(db, CHANNELS_COLLECTION);
    const q = query(
      channelsRef,
      where('workflowId', '==', workflowId),
      where('entryId', '==', entryId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const channels: ChatChannelInstance[] = [];

    snapshot.forEach((doc) => {
      channels.push({
        id: doc.id,
        ...doc.data()
      } as ChatChannelInstance);
    });


    return channels;
  } catch (error) {
    log.error('Error getting all channels for entry:', error);
    return [];
  }
}
